import { BotSequenceType } from "../../BotSequences/BotSequence.Enums";
import { LocationVarKey } from "../Generated/LocationVarKey.Data";
import { VarDef } from "../Generated/VarDef.Data";
import { VarKey } from "../Generated/VarKey.Data";

/**
 * The legacy sequence settings the migrator's Sequences step reads (ENG-3739), and how their
 * placeholders become Snowy text. Legacy keys follow naming patterns per sequence type and day
 * ("slot"); every builder below keeps only names that exist in `LocationVarKey`, so a pattern that
 * does not apply to a type simply yields nothing.
 *
 * What legacy actually sends is decided by each org's quests, not by these keys alone. The step
 * reads the quests for that (`CortexSequenceQuestScan.ts`); this file only names the keys.
 * Design: docs/snowy/plans/2026-10-02-legacy-sequences-migration.md.
 */

/** Per-day entry quests. The step never follows a link from one day's quest into another day's. */
export const LEGACY_SEQUENCE_DAY_QUEST_SLUG_RE = /^(past-due|abandoned-cart|move-in|move-out)-/;

const TYPE_NAME: Record<BotSequenceType, string> = {
  [BotSequenceType.PAST_DUE]: "PastDue",
  [BotSequenceType.ABANDONED_CART]: "AbandonedCart",
  [BotSequenceType.MOVE_IN]: "MoveIn",
  [BotSequenceType.MOVE_OUT]: "MoveOut"
};

const TYPE_BY_NAME: Record<string, BotSequenceType> = {
  PastDue: BotSequenceType.PAST_DUE,
  AbandonedCart: BotSequenceType.ABANDONED_CART,
  MoveIn: BotSequenceType.MOVE_IN,
  MoveOut: BotSequenceType.MOVE_OUT
};

/** One legacy day slot, identified by the switch the old scheduler checks for it. */
export interface LegacySequenceSlot {
  sequenceType: BotSequenceType;
  /** The day infix legacy keys use: "0" (Day 0), "p3" (Day 3), "Recurring". */
  token: string;
  /** What the day quests save as `User:EntityDate`: "Day 0", "Day 3", "Recurring". */
  entityDate: string;
  /** The scheduler switch, e.g. `SequencePastDueDay03Enabled`. */
  enabledKey: string;
  /** The two-digit day of the switch name ("03"), or null for Recurring. A UI slot, not the day number. */
  slotDay: string | null;
}

const ENABLED_KEY_RE = /^Sequence(PastDue|AbandonedCart|MoveIn|MoveOut)(?:Day(\d{2})|(Recurring))Enabled$/;

/**
 * The slot a legacy `sequence` row belongs to, from its `conditions.sequenceEnabledVariable`.
 * The day NUMBER is never taken from here: rows sharing a switch can fire on different days
 * (`conditions.daysPastDue/daysAgo/daysSince` is the truth).
 */
export function legacySequenceSlotFor(enabledKey: string | null | undefined): LegacySequenceSlot | null {
  const match = ENABLED_KEY_RE.exec(enabledKey ?? "");
  if (!match) return null;
  const sequenceType = TYPE_BY_NAME[match[1]];
  if (match[3]) {
    return { sequenceType, token: "Recurring", entityDate: "Recurring", enabledKey: enabledKey as string, slotDay: null };
  }
  const day = Number(match[2]);
  return { sequenceType, token: day === 0 ? "0" : `p${day}`, entityDate: `Day ${day}`, enabledKey: enabledKey as string, slotDay: match[2] };
}

let locationVarKeyNames: Set<string> | null = null;
/** True when `name` is a `LocationVarKey`. Built lazily: enum members are not read at module load. */
export function isLegacyLocationVarKey(name: string): boolean {
  if (!locationVarKeyNames) locationVarKeyNames = new Set<string>(Object.values(LocationVarKey));
  return locationVarKeyNames.has(name);
}

const existing = (name: string): string | undefined => (isLegacyLocationVarKey(name) ? name : undefined);
const existingAll = (names: string[]): string[] => names.filter(isLegacyLocationVarKey);

/** One legacy message of a day, in send order, with the switches that can replace or skip it. */
export interface LegacySequenceMessageKeys {
  key: string;
  /** Its own on/off switch (messages 2 and 3). */
  enabledKey?: string;
  /** The version sent instead when "Use Shortlinks" is on (message 2 of Past Due and Abandoned Cart). */
  shortlinkKey?: string;
}

/** Every legacy key of one day slot, as far as the naming patterns reach. */
export interface LegacySequenceSlotKeys {
  messages: LegacySequenceMessageKeys[];
  smsEnabled?: string;
  voiceEnabled?: string;
  emailEnabled?: string;
  /** The link a day's shortlink (`User:ShortURL`) is made from. */
  smsLinkToShorten?: string;
  emailLinkToShorten?: string;
  /** Guidance for the legacy call agent (an instruction inside its prompt, not spoken word for word). */
  voiceMessage?: string;
  /** SMS / Email / Both, on types that choose channels with a dropdown. */
  method?: string;
  /** Day switches some quests check besides the scheduler switch (`FMSplushas…`, `…ActuallyEnabled`). */
  daySwitches: string[];
  /** Review and referral asks: not migrated (the Unmigrated content step archives them). */
  asks: string[];
  /** Send time "HH:mm", or the Day 0 window. */
  time?: string;
  minTime?: string;
  maxTime?: string;
}

/** The legacy keys of one day slot. */
export function legacySequenceSlotKeys(slot: LegacySequenceSlot): LegacySequenceSlotKeys {
  const type = TYPE_NAME[slot.sequenceType];
  const plus = `FMSplus${slot.token}${type}`;
  const has = `FMSplushas${slot.token}${type}`;
  // Abandoned Cart messages live under the "has" prefix; the other types under the plain one.
  const messageBase = slot.sequenceType === BotSequenceType.ABANDONED_CART ? has : plus;

  const messages: LegacySequenceMessageKeys[] = [];
  const first = existing(`${messageBase}1`);
  if (first) messages.push({ key: first });
  const second = existing(`${messageBase}2`);
  if (second) messages.push({ key: second, enabledKey: existing(`${messageBase}2Enabled`), shortlinkKey: existing(`${plus}2Shortlink`) });
  const third = existing(`${messageBase}3`);
  if (third) messages.push({ key: third, enabledKey: existing(`${messageBase}3Enabled`) });

  const dayName = slot.slotDay !== null ? `Day${slot.slotDay}` : "Recurring";
  const scheduler = `Sequence${type}${dayName}`;
  return {
    messages,
    smsEnabled: existing(`${plus}SMSEnabled`),
    voiceEnabled: existing(`${plus}VoiceEnabled`),
    emailEnabled: existing(`${plus}EmailEnabled`),
    smsLinkToShorten: existing(`${plus}SMSShortlink`),
    emailLinkToShorten: existing(`${plus}EmailShortlink`),
    voiceMessage: existing(`${plus}VoiceMessage`),
    method: existing(`${has}Method`),
    daySwitches: existingAll([has, `${scheduler}ActuallyEnabled`]),
    asks: existingAll([`${has}Review`, `${has}Referral`, `${has}Referral1`, `${plus}Review`, `${plus}Referral`, `${plus}Referral1`]),
    time: existing(`${scheduler}Time`),
    minTime: existing(`${scheduler}MinTime`),
    maxTime: existing(`${scheduler}MaxTime`)
  };
}

/** Settings that apply to every day of a type. */
export interface LegacySequenceTypeKeys {
  shortlinksEnabled?: string;
  fmsNotes?: string;
  spotPaymentPages?: string;
  /** Staff receipt emails: not migrated. */
  receipts?: string;
}

export function legacySequenceTypeKeys(sequenceType: BotSequenceType): LegacySequenceTypeKeys {
  switch (sequenceType) {
    case BotSequenceType.PAST_DUE:
      return {
        shortlinksEnabled: existing("FMSplusPastDueShortlinksEnabled"),
        fmsNotes: existing("HasFMSPlusPastDueNotes"),
        spotPaymentPages: existing("HasPastDueSpotPaymentPages"),
        receipts: existing("WantsCronReceiptsPastDuePayments")
      };
    case BotSequenceType.ABANDONED_CART:
      return {
        shortlinksEnabled: existing("FMSplusAbandonedCartsShortlinksEnabled"),
        fmsNotes: existing("HasFMSPlusAbandonedCartNotes"),
        receipts: existing("WantsCronReceiptsAbandonedCarts")
      };
    case BotSequenceType.MOVE_IN:
      return { fmsNotes: existing("HasFMSPlusMoveInNotes"), receipts: existing("WantsCronReceiptsMoveIn") };
    case BotSequenceType.MOVE_OUT:
      return { fmsNotes: existing("HasFMSPlusMoveOutNotes"), receipts: existing("WantsCronReceiptsMoveOut") };
  }
}

/** Every legacy day slot that has a scheduler switch, per type. */
export function legacySequenceSlots(): LegacySequenceSlot[] {
  return Object.values(LocationVarKey)
    .map((key) => legacySequenceSlotFor(key))
    .filter((slot): slot is LegacySequenceSlot => slot !== null);
}

/**
 * The legacy keys the Sequences step migrates, so the Unmigrated content report no longer lists
 * them as gaps (`EXTERNALLY_OWNED_KEY_FAMILIES`). Review and referral asks and staff receipts are
 * left out on purpose: nothing migrates them, so that report must keep archiving them.
 */
export function legacySequenceMigratedKeys(): string[] {
  const keys = new Set<string>();
  const add = (key: string | undefined): void => {
    if (key) keys.add(key);
  };
  for (const slot of legacySequenceSlots()) {
    add(slot.enabledKey);
    const slotKeys = legacySequenceSlotKeys(slot);
    for (const message of slotKeys.messages) {
      add(message.key);
      add(message.enabledKey);
      add(message.shortlinkKey);
    }
    [
      slotKeys.smsEnabled,
      slotKeys.voiceEnabled,
      slotKeys.emailEnabled,
      slotKeys.smsLinkToShorten,
      slotKeys.emailLinkToShorten,
      slotKeys.voiceMessage,
      slotKeys.method,
      slotKeys.time,
      slotKeys.minTime,
      slotKeys.maxTime,
      ...slotKeys.daySwitches
    ].forEach(add);
  }
  for (const sequenceType of Object.values(BotSequenceType)) {
    const typeKeys = legacySequenceTypeKeys(sequenceType);
    add(typeKeys.shortlinksEnabled);
    add(typeKeys.fmsNotes);
    add(typeKeys.spotPaymentPages);
  }
  return [...keys].sort();
}

// ── Placeholders ─────────────────────────────────────────────────────────────────────

/**
 * How one legacy placeholder (`#*Key*#`) becomes Snowy text, for the ones the shared
 * `convertLegacyTokens` does not cover (production counts from the plan's placeholder table).
 */
export type LegacySequenceTokenRule =
  /** Replace with this Snowy token. */
  | { kind: "brace"; to: string }
  /** Drop the line the token is on (Swivl's own default wording, not the operator's). */
  | { kind: "dropLine" }
  /** Write the scope's resolved legacy value into the text (one account, one value). */
  | { kind: "inline" }
  /** The full link the day's legacy shortlink was made from. */
  | { kind: "shortUrl" }
  /** A live per-tenant payment link Snowy text has no variable for: the pay-online link, flagged. */
  | { kind: "paymentLink" }
  /** Only in messages legacy never sends; flagged if it ever reaches a sent message. */
  | { kind: "neverSent" };

const FIRST_NAME = '{{User:FirstName|"there"}}';
const PAY_ONLINE = "{{Location:CortexURLPayOnline}}";
const ORG_NAME = "{{Location:CortexOrgName}}";

export const LEGACY_SEQUENCE_TOKEN_RULES: Readonly<Record<string, LegacySequenceTokenRule>> = {
  "User:Name": { kind: "brace", to: FIRST_NAME },
  "User:FirstName": { kind: "brace", to: FIRST_NAME },
  "Org:Name": { kind: "brace", to: ORG_NAME },
  // Written at each group by the step, so the token resolves to the group's name there.
  "Group:Name": { kind: "brace", to: ORG_NAME },
  User_AccountBalance: { kind: "brace", to: "{{Lease:Balance}}" },
  "Location:PaymentURL": { kind: "brace", to: PAY_ONLINE },
  PaymentURL: { kind: "brace", to: PAY_ONLINE },
  // On/off settings typed where the pay link was meant (tenants get "1" today).
  "Location:HasPayOnlineURL": { kind: "brace", to: PAY_ONLINE },
  "Location:HasPayBillSpotPaymentPages": { kind: "brace", to: PAY_ONLINE },
  "Location:SpotLocationSlug": { kind: "brace", to: "{{Location:CortexSpotLocationSlug}}" },
  "Location:SpotBrandSlug": { kind: "brace", to: "{{Location:CortexSpotBrandSlug}}" },
  "Location:SpotReferralURL": { kind: "brace", to: "{{Location:CortexSpotReferralURL}}" },
  "Location:OfficeHours": { kind: "brace", to: "{{Location:CortexFacilityOfficeHours}}" },
  SpaceDimensions: { kind: "brace", to: "{{Unit:Dimensions}}" },
  UserPaymentDueDate: { kind: "brace", to: "{{Lease:DueDate}}" },
  SpaceRate: { kind: "brace", to: "{{Unit:Rate}}" },
  "User:DaysPastDue": { kind: "brace", to: "{{Lease:DaysPastDue}}" },
  "Location:LockTypes": { kind: "dropLine" },
  "Location:ReferralIncentive": { kind: "inline" },
  "User:ShortURL": { kind: "shortUrl" },
  "User:CollectionsAgentSMSPaymentURL": { kind: "paymentLink" },
  "Location:GoLocalFID": { kind: "neverSent" },
  SpaceId: { kind: "neverSent" }
};

/** Tenant-data namespaces whose tokens carry over unchanged when the key exists (`#*Lease:Balance*#` → `{{Lease:Balance}}`). */
const PASS_THROUGH_NAMESPACES = ["Lease:", "Unit:", "User:", "Tenant:"];

/**
 * The rule for one legacy placeholder key (as written between `#*` and `*#`, minus any `[label]`),
 * or undefined when the shared converter should handle it (Location tokens) or nothing can.
 */
export function legacySequenceTokenRule(key: string): LegacySequenceTokenRule | undefined {
  const explicit = LEGACY_SEQUENCE_TOKEN_RULES[key];
  if (explicit) return explicit;
  if (PASS_THROUGH_NAMESPACES.some((namespace) => key.startsWith(namespace)) && Object.prototype.hasOwnProperty.call(VarDef, key as VarKey)) {
    return { kind: "brace", to: `{{${key}}}` };
  }
  return undefined;
}
